import {Component, Inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {UtilsService} from "../../shared/utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ApiRequestService} from "../../shared/api-request.service";
import {AppService} from "../../app.service";
import {NgForm, NgModel} from "@angular/forms";
import {VendorModel} from "../../models/vendor.model";
import moment from "moment";
import {
  NetworkedEmployeesSelectorComponent
} from "../../shared/networked-employees-selector/networked-employees-selector.component";
import {ResolvedAddress} from "../../shared/address-lookup/address-lookup.component";
import {RBACService} from "../../shared/rbac.service";

@Component({
  selector: 'app-vendors-edit',
  templateUrl: './vendors-edit.component.html',
  styleUrls: ['./vendors-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorsEditComponent implements OnInit {

  inProgress: boolean = false;
  vendor: VendorModel = new VendorModel();
  selectedFiles: File[] = [];

  selectedPreQualifiedDate: moment.Moment;
  selectedInsuranceExpiryDate: moment.Moment;
  insuranceLastVerifiedDate: moment.Moment | null;

  // Today, used to cap the "insurance verified" date so future dates cannot be selected.
  readonly today: moment.Moment = moment();

  // Mobile field Country Code selector values
  mobile_input_id: string = 'phone-1';
  mobile_number: string = '';
  mobile_country_code: string = '';
  mobile_dial_code: string = '';
  mobile_e164: string = '';
  mobile_error_state: boolean = false;

  urlRegex: RegExp = /^https?:\/\/([A-Za-z0-9-]+\.)+[A-Za-z]{2,}(\/.*)?$/;
  emailPattern: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialogRef: MatDialogRef<VendorsEditComponent>,
    private api: ApiRequestService,
    private app: AppService,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    const vendor_id = this.dialogData?.vendor_id;

    // Resolve the RBAC permission flags first so they are always set, even when the access guard
    // below short circuits the rest of this method.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_VENDORS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_VENDORS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_VENDORS, RBACService.RBAC_A.A_DELETE)
    ], true);

    const isEdit: boolean = typeof vendor_id === 'number' && vendor_id > 0;

    // Editing an existing vendor needs the edit permission; creating a new one needs create.
    if ( (isEdit && !this.canEdit) || (!isEdit && !this.canCreate) ) {
      this.rbac.showGenericAccessDeniedMessage(
        isEdit
          ? 'You do not have permission to edit vendors.'
          : 'You do not have permission to create vendors.'
      );
      this.dialogRef.close();
      return;
    }

    if (isEdit) {
      this.find(vendor_id);
    }
  }

  /**
   * Pre-populate the structured address fields when an address is selected from the lookup.
   *
   * @param {ResolvedAddress} address - The resolved address from the lookup component.
   * @return {void}
   */
  onAddressSelected(address: ResolvedAddress): void {
    this.vendor.address = address.address;
    this.vendor.suburb = address.suburb;
    this.vendor.city = address.city;
    this.vendor.postal_code = address.postalCode;
    this.vendor.country = address.country;
  }

  /**
   * Get Output values from phone input component.
   *
   * @param {any} event - The event object containing phone details.
   * @return {void}
   */
  getOutputPhoneDetails(event: any): void {
    if(!event) {
      return;
    }

    this.mobile_number = event.number ?? '';
    this.mobile_country_code = event.countryCode ?? '';
    this.mobile_dial_code = event.dialCode ?? '';
    this.mobile_e164 = event.e164Number ?? '';
    this.mobile_error_state = event.errorState ?? true;
  }

  /**
   * Updates the selected pre-qualified date when it changes.
   *
   * @param {any} value - The new selected pre-qualified date.
   * @return {void}
   */
  onPreQualifiedDateChange(value: any): void {
    this.selectedPreQualifiedDate = value;
  }

  /**
   * Updates the selected insurance expiry date when it changes.
   *
   * @param {any} value - The new selected insurance expiry date.
   * @return {void}
   */
  onInsuranceExpiryDateChange(value: any): void {
    this.selectedInsuranceExpiryDate = value;
  }

  /**
   * Validate the form input and save the data.
   *
   * @param {NgForm} form
   * @param {boolean} [autoCloseDialog=true] - Flag indicating whether to automatically close the dialog after submission.
   * @return {void}
   */
  onSubmit(form: NgForm, autoCloseDialog: boolean = true): void {
    // Check if the button was already pressed.
    if ( this.inProgress ) {
      return;
    }

    // Check form validation.
    if ( !form.valid ) {
      this.utils.showFormValidationError('Please enter all required fields.');
      return;
    }

    // Guard the action that will actually be performed: an existing vendor is updated, a new one is
    // created, and those are separate permissions.
    if ( this.vendor.id ) {
      if ( !this.canEdit ) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit vendors.');
        return;
      }
    } else {
      if ( !this.canCreate ) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to create vendors.');
        return;
      }
    }

    // Check Error states of phone input then continue submission
    if (this.mobile_error_state) {
      if(this.mobile_number.length < 3) {
        this.utils.showFormValidationError('A Phone number must be at least 3 characters long.');
      } else {
        this.utils.showFormValidationError();
      }
      return;
    }

    // Update Vendor details with Mobile field Country Code selector values
    this.vendor.phone = this.mobile_number;
    this.vendor.phone_country_code = this.mobile_country_code;
    this.vendor.phone_dial_code = this.mobile_dial_code;
    this.vendor.phone_e164 = this.mobile_e164;

    this.inProgress = true;

    if(!this.vendor.is_prequalified) {
      this.vendor.prequalified_date = null;
    }

    // Check if a Pre-Qualified date was selected before attempting to use formatting functions.
    if ( this.vendor.prequalified_date && this.selectedPreQualifiedDate ) {
      this.vendor.prequalified_date = this.selectedPreQualifiedDate.format('YYYY-MM-DD');
    }

    // Check if an Insurance Expiry date was selected before attempting to use formatting functions.
    if ( this.vendor.insurance_valid_till && this.selectedInsuranceExpiryDate ) {
      this.vendor.insurance_valid_till = this.selectedInsuranceExpiryDate.format('YYYY-MM-DD');
    }

    // Check if an Insurance Verified date was selected, storing it as a unix timestamp (date only, start of day in the account timezone).
    if ( this.insuranceLastVerifiedDate && moment.isMoment(this.insuranceLastVerifiedDate) ) {
      this.vendor.insurance_last_verified_date = moment.tz(this.insuranceLastVerifiedDate.format('YYYY-MM-DD'), 'YYYY-MM-DD', this.app.account.timezone).startOf('day').unix();
    } else {
      this.vendor.insurance_last_verified_date = null;
    }

    !this.vendor.id ? this.create(autoCloseDialog) : this.update(autoCloseDialog);
  }

  /**
   * Finds a Vendor by its ID.
   *
   * @param {number} id - The ID of the Vendor.
   * @private
   * @return {void}
   */
  private find(id: number): void {
    // Make the API request.
    this.api.makeRequest('get', `v2/vendors/${id}`)
      .then((response) => {
        this.vendor = response.data as VendorModel;

        // Populate the date-only picker from the stored unix timestamp.
        if ( this.vendor.insurance_last_verified_date ) {
          this.insuranceLastVerifiedDate = moment.unix(this.vendor.insurance_last_verified_date);
        }
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Updates the Vendor with new data.
   *
   * @param {boolean} autoCloseDialog - A boolean flag indicating whether to automatically close the dialog after update. Default is true.
   * @return void
   */
  private update(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('put', `v2/vendors/${this.vendor.id}`, this.vendor)
      .then((response) => {
        this.vendor = response.data as VendorModel;
        this.onSuccess(`Updated: "${this.vendor.name}"`, autoCloseDialog);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Creates a new Vendor.
   *
   * @param {boolean} autoCloseDialog - A boolean flag indicating whether to automatically close the dialog after creation. Default is true.
   * @return {void}
   */
  private create(autoCloseDialog: boolean = true): void {
    this.api
      .makeRequest('post', 'v2/vendors', this.vendor)
      .then((response) => {
        this.vendor = response.data as VendorModel;
        this.onSuccess(`New Vendor created.`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => {
        // Upload files.
        this.uploadSelectedFiles(this.vendor.id, (): void => {
          this.inProgress = false;
        });
      });
  }

  /**
   * Handles the success message of the API request or custom.
   *
   * @param {string} message - The message to display in the toast.
   * @param {boolean} autoCloseDialog - Whether the dialog should be automatically closed. Default value is true.
   * @return {void}
   */
  private onSuccess(message: string, autoCloseDialog: boolean = true): void {
    this.utils.showToast(message);
    // Check if the dialog should be closed.
    if ( autoCloseDialog ) {
      this.dialogRef.close(this.vendor);
    }
  }

  /**
   * Archives the Vendor.
   *
   * @return {void}
   */
  onDelete(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive vendors.');
      return;
    }
    this.utils.showModal(
      `Archive Vendor`,
      `Are you sure you want to archive Vendor?`,
      () => {
        this.api.makeRequest('delete', `v2/vendors/${this.vendor.id}`)
          .then((response) => {
            this.utils.showToast(`"${this.vendor.name}" has been archived.`);
            this.dialogRef.close(true);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Select last insurance verifier to associate with this vendor.
   *
   * @return {void}
   */
  onSelectInsuranceVerifier(): void {
    this.utils.showComponentDialog(
      NetworkedEmployeesSelectorComponent,
      {
        multiple: false,
        selected: [this.vendor.insurance_last_verified_by],
        selectedAccountId: this.app.account.id
      },
      {
        width: '1200px'
      },
      (userId: number) => {

        if ( typeof userId == 'undefined' ) {
          return;
        }

        this.vendor.insurance_last_verified_by = userId;
      }
    );
  }

  /**
   * Uploads the selected files to the server for the specified resource ID.
   * Provides a callback function to be executed after the upload process completes.
   *
   * @private
   * @returns {void}
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/vendor/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    } else {
      if ( typeof callback === 'function' ) {
        callback();
      }
    }
  }

  /**
   * If it is a new vendor, add the files to the selected files array.
   * Existing vendor files will be handled by the file manager.
   *
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Check if the provided value is a valid website URL.
   *
   * @param {string | null | undefined} value - The value to be validated as a website URL.
   * @return {boolean} Returns true if the value is a valid website URL or empty, false otherwise.
   */
  isValidWebsite(value: string | null | undefined): boolean {
    const v = (value ?? '').trim();

    // Empty is valid
    if (!v){
      return true;
    }

    return this.urlRegex.test(v);
  }

  /**
   * Updates the error state of a website input field based on the validity of the input value.
   *
   * @param {string} value - The input value to be validated as a website URL.
   * @param {NgModel} websiteModel - The NgModel object associated with the website input field.
   * @return {void}
   */
  onWebsiteChange(value: string, websiteModel: NgModel): void {
    const valid = this.isValidWebsite(value);

    // Merge with any existing errors
    const currentErrors = websiteModel.control.errors ?? {};

    if (!valid) {
      websiteModel.control.setErrors({ ...currentErrors, invalidWebsite: true });
    } else {
      // Remove invalidWebsite error, keep others
      const { invalidWebsite, ...rest } = currentErrors;
      websiteModel.control.setErrors(Object.keys(rest).length ? rest : null);
    }
  }
}
